"use client";
import { cn } from "@/lib/utils";
import React, { useEffect, useMemo, useRef } from "react";

interface TextShimmerProps {
  children: string;
  as?: React.ElementType;
  className?: string;
  duration?: number;
  spread?: number;
}

export function TextShimmer({
  children,
  as: Component = "p",
  className,
  duration = 2,
  spread = 2,
}: TextShimmerProps) {
  const dynamicSpread = useMemo(() => {
    return children.length * spread;
  }, [children, spread]);

  const styleRef = useRef<HTMLStyleElement | null>(null);
  const animationName = useRef(`shimmer-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    // Create and inject keyframes dynamically
    if (!styleRef.current) {
      const style = document.createElement('style');
      style.textContent = `
        @keyframes ${animationName.current} {
          from {
            background-position: 100% center;
          }
          to {
            background-position: 0% center;
          }
        }
      `;
      document.head.appendChild(style);
      styleRef.current = style;
    }

    return () => {
      // Cleanup on unmount
      if (styleRef.current && document.head.contains(styleRef.current)) {
        document.head.removeChild(styleRef.current);
        styleRef.current = null;
      }
    };
  }, []);

  return (
    <Component
      className={cn(
        "relative inline-block bg-[length:250%_100%,auto] bg-clip-text",
        "text-transparent [--base-color:#a1a1aa] [--base-gradient-color:#000]",
        "[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--base-gradient-color),#0000_calc(50%+var(--spread)))] [background-repeat:no-repeat,padding-box]",
        "dark:[--base-color:#71717a] dark:[--base-gradient-color:#ffffff] dark:[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--base-gradient-color),#0000_calc(50%+var(--spread)))]",
        className,
      )}
      style={
        {
          "--spread": `${dynamicSpread}px`,
          backgroundImage: `var(--bg), linear-gradient(var(--base-color), var(--base-color))`,
          animation: `${animationName.current} ${duration}s linear infinite`,
        } as React.CSSProperties
      }
    >
      {children}
    </Component>
  );
}
